import { Button, Flex, FlexProps, Text } from '@chakra-ui/react'
import { ReactNode } from 'react'

import { IRowSelection } from './useRowSelection'

export interface IBulkActionBarProps extends Omit<FlexProps, 'children'> {
  selection: IRowSelection
  /**
   * O substantivo no **singular**, minúsculo — a mesma convenção do `ConfirmDialog` e do
   * `TableEmptyRow`. A barra escreve `3 entregas selecionadas`.
   */
  entity?: string
  /** Plural irregular, para o substantivo que não faz o plural em `-s`. */
  entityPlural?: string
  /** Concordância do artigo e do particípio: `3 entregas selecionadas`. */
  feminine?: boolean
  /** Os botões da ação em massa. */
  children: ReactNode
  /**
   * Com `false` a barra some do fluxo quando não há nada marcado, em vez de ficar invisível
   * ocupando o mesmo espaço. É para a tela que já tem outro bloco nesse lugar — o "Enviar para
   * todos os inscritos" do Painel do Evento —, onde reservar o espaço deixaria uma faixa morta.
   */
  reserveSpace?: boolean
}

/**
 * A barra que aparece acima da tabela quando há linhas marcadas: quantas são, o que dá para
 * fazer com elas e a saída ("Limpar seleção").
 *
 * As ações em massa moravam em **`ActionFab` flutuantes** em duas das três telas — dois
 * empilhados em Entrega de Trabalho, um em Base de Leads —, e isso é o lugar errado por três
 * motivos que a barra em linha do Painel do Evento já resolvia: o FAB fica no canto inferior
 * direito, longe das linhas que a pessoa acabou de marcar; ele **não diz quantas** estão
 * marcadas; e empilha com o FAB de criar, que é a ação principal da tela — em Entrega de
 * Trabalho eram três botões redondos, um sobre o outro, dois deles destrutivos.
 *
 * Com contagem nenhuma a barra fica **invisível, mas no fluxo**: sumir dele fazia a tabela descer
 * pela altura da barra no instante em que a primeira linha era marcada, e a linha recém-clicada
 * mudava de lugar sob o cursor. Renderizar o conteúdo de verdade sob `visibility: hidden` é o que
 * garante a altura exata — e tira os botões do foco e da árvore de acessibilidade junto.
 * `reserveSpace={false}` é a saída para a tela que já tem outro bloco nesse espaço.
 */
export default function BulkActionBar({
  selection,
  entity = 'registro',
  entityPlural,
  feminine,
  children,
  reserveSpace = true,
  ...rest
}: IBulkActionBarProps) {
  const { count } = selection
  const isEmpty = count === 0

  if (isEmpty && !reserveSpace) return null

  const noun = count === 1 ? entity : (entityPlural ?? `${entity}s`)
  const participle = `selecionad${feminine ? 'a' : 'o'}${count === 1 ? '' : 's'}`

  return (
    <Flex align="center" gap={3} mb={4} wrap="wrap" visibility={isEmpty ? 'hidden' : 'visible'} {...rest}>
      <Text fontSize="sm" fontWeight="semibold" color="gray.700">
        {count} {noun} {participle}
      </Text>
      {children}
      <Button size="sm" variant="ghost" colorScheme="gray" onClick={selection.clear}>
        Limpar seleção
      </Button>
    </Flex>
  )
}
